import {
  Board,
  BoardHeader,
  BoardSearchResult
} from "../../assets/GlobalStyles";
import React, {
  useEffect,
  useState
} from "react";
import styled from "styled-components";
import {
  findCreativeGroupList
} from "../../services/campaign/CreativeManageAxios";
import {
  tokenResultAtom
} from "../login/entity/Common";
import CampaignBoard from "../dash_board/CampaignBoard";
import {useAtom} from "jotai";
import {
  adverStatusAtom
} from "../dash_board/entity/Campaign";
import {toast} from "react-toastify";

export function ManageCreative() {
  const [tokenUserInfo] =
      useAtom(tokenResultAtom);

  const [
    creativeData,
    setCreativeData
  ] = useAtom(adverStatusAtom);

  const [keyword, setKeyword] =
      useState("");

  const [isSearching, setIsSearching] =
      useState(false);

  const loadCreativeGroups =
      async searchKeyword => {
        if (isSearching) {
          return;
        }

        setIsSearching(true);

        try {
          const response =
              await findCreativeGroupList(
                  searchKeyword.trim()
              );

          const creativeGroupDtos =
              response?.creativeGroupDtos ?? [];

          setCreativeData(
              creativeGroupDtos.filter(
                  item =>
                      item.status === "NORMAL"
              )
          );
        } catch (error) {
          console.error(
              "캠페인 및 소재 목록 조회 실패:",
              error
          );

          toast.error(
              "캠페인 및 소재 정보를 불러오는 중 오류가 발생했습니다.",
              {
                toastId:
                    "manageCreativeListLoadError"
              }
          );
        } finally {
          setIsSearching(false);
        }
      };

  useEffect(() => {
    if (
        tokenUserInfo?.role === "NORMAL"
    ) {
      return;
    }

    loadCreativeGroups("");

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchKeyword = () => {
    loadCreativeGroups(keyword);
  };

  const handleClearKeyword = () => {
    setKeyword("");
    loadCreativeGroups("");
  };

  return (
      <Board>
        <BoardHeader>
          캠페인 및 소재 관리
        </BoardHeader>

        <BoardSearchResult>
          <SettingCard $compact>
            <CompactSearchHeader>
              <SectionTitle>
                캠페인 검색
              </SectionTitle>
            </CompactSearchHeader>

            <InnerBox>
              <FieldRow>
                <FieldContent>
                  <SearchRow>
                    <SearchInputBox>
                      <SearchIcon
                          aria-hidden="true"
                      >
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                        >
                          <path
                              fill="currentColor"
                              d="m21 19-4.35-4.35a7.5 7.5 0 1 0-2 2L19 21l2-2ZM5 10.5a5.5 5.5 0 1 1 11 0 5.5 5.5 0 0 1-11 0Z"
                          />
                        </svg>
                      </SearchIcon>

                      <KeywordInput
                          type="text"
                          value={keyword}
                          placeholder="광고주명 및 아이디 검색"
                          disabled={isSearching}
                          onChange={event =>
                              setKeyword(
                                  event.target.value
                              )
                          }
                          onKeyDown={event => {
                            if (
                                event.key === "Enter"
                            ) {
                              event.preventDefault();
                              handleSearchKeyword();
                            }
                          }}
                      />

                      {keyword && (
                          <KeywordClearButton
                              type="button"
                              aria-label="검색어 지우기"
                              title="검색어 지우기"
                              disabled={isSearching}
                              onClick={
                                handleClearKeyword
                              }
                          >
                            <span />
                          </KeywordClearButton>
                      )}
                    </SearchInputBox>

                    <SearchButton
                        type="button"
                        disabled={isSearching}
                        onClick={
                          handleSearchKeyword
                        }
                    >
                      {isSearching
                          ? "검색 중"
                          : "검색"}
                    </SearchButton>
                  </SearchRow>

                </FieldContent>
              </FieldRow>
            </InnerBox>
          </SettingCard>

          <SettingCard>
            <SectionHeader>
              <SectionTitleGroup>
                <SectionTitle>
                  캠페인 및 소재 현황
                </SectionTitle>
              </SectionTitleGroup>

              <SectionGuide>
                캠페인을 선택해 연결된 광고그룹과
                소재 정보를 확인합니다.
              </SectionGuide>
            </SectionHeader>

            <CampaignResultBox>
              {isSearching && (
                  <LoadingOverlay>
                    <LoadingSpinner />

                    <span>
                  캠페인 정보를 불러오고 있습니다.
                </span>
                  </LoadingOverlay>
              )}

              <CampaignBoard />
            </CampaignResultBox>
          </SettingCard>
        </BoardSearchResult>
      </Board>
  );
}

const SettingCard = styled.section`
  margin-bottom: ${({$compact}) => $compact ? "14px" : "22px"};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 14px;

  @media (max-width: 760px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }
`;

const SectionTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
`;

const SectionTitle = styled.div`
  color: #222;
  font-size: 17px;
  font-weight: 700;
`;

const SectionGuide = styled.div`
  color: #888;
  font-size: 14px;
  line-height: 1.5;
  text-align: right;

  @media (max-width: 760px) {
    text-align: left;
  }
`;

const InnerBox = styled.div`
  padding: 10px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background-color: #f9fafb;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const FieldLabel = styled.div`
  width: 150px;
  min-width: 150px;
  padding-top: 10px;
  color: #444;
  font-size: 16px;
  font-weight: 600;

  @media (max-width: 900px) {
    width: auto;
    min-width: 0;
    padding-top: 0;
  }
`;

const FieldContent = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 0;
`;

const SearchRow = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;

  @media (max-width: 620px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const SearchInputBox = styled.div`
  position: relative;
  width: 100%;
  min-width: 280px;
  max-width: 440px;

  @media (max-width: 700px) {
    min-width: 0;
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  top: 50%;
  left: 13px;
  z-index: 1;
  display: inline-flex;
  color: #999;
  pointer-events: none;
  transform: translateY(-50%);
`;

const KeywordInput = styled.input`
  width: 100%;
  height: 36px;
  box-sizing: border-box;
  padding: 0 42px 0 39px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background-color: #fff;
  color: #444;
  font-size: 14px;
  outline: none;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: #f1d4c2;
  }

  &:focus {
    border-color: #f5811f;
    background-color: #fff;
  }

  &:disabled {
    background-color: #f5f5f5;
    color: #999;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const KeywordClearButton = styled.button`
  position: absolute;
  top: 50%;
  right: 10px;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background-color: #f1f1f1;
  cursor: pointer;
  transform: translateY(-50%);

  &::before,
  &::after {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 9px;
    height: 1px;
    background-color: #888;
    content: "";
  }

  &::before {
    transform:
      translate(-50%, -50%)
      rotate(45deg);
  }

  &::after {
    transform:
      translate(-50%, -50%)
      rotate(-45deg);
  }

  &:hover:not(:disabled) {
    background-color: #fff4e8;
  }

  &:hover:not(:disabled)::before,
  &:hover:not(:disabled)::after {
    background-color: #f5811f;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  & > span {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
  }
`;

const SearchButton = styled.button`
  min-width: 72px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid #f5811f;
  border-radius: 6px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #ffe8d2;
  }

  &:disabled {
    border-color: #ddd;
    background-color: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }

  @media (max-width: 620px) {
    width: 100%;
  }
`;

const InputGuide = styled.div`
  color: #999;
  font-size: 13px;
  line-height: 1.4;
`;

const CampaignResultBox = styled.div`
  position: relative;
  min-height: 240px;
  overflow: hidden;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background-color: #fff;
  padding: 10px 15px;

  /*
   * CampaignBoard의 최상위 여백과 배경이 있을 경우
   * 현재 카드 안에서 자연스럽게 보이도록 정리합니다.
   */
  & > div {
    margin: 0;
    border: 0;
    border-radius: 0;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  background-color: rgba(255, 255, 255, 0.78);
  color: #777;
  font-size: 13px;
  backdrop-filter: blur(1px);
`;

const LoadingSpinner = styled.span`
  width: 18px;
  height: 18px;
  box-sizing: border-box;
  border: 2px solid #f3d5c0;
  border-top-color: #f5811f;
  border-radius: 50%;
  animation: manage-creative-spin 0.7s linear infinite;

  @keyframes manage-creative-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

/*
 * 다른 화면에서 기존 스타일 컴포넌트를 import하고 있을 가능성을
 * 고려해 export 이름은 유지합니다.
 */
export const CustomTable = styled.div`
  width: 100%;
  overflow: hidden;
  border: 1px solid #e5e5e5;
  border-radius: 7px;
  background-color: #fff;
`;

export const CustomTableHeader = styled.div`
  display: grid;
  grid-template-columns:
    repeat(4, minmax(0, 1fr));
  min-width: 680px;
  min-height: 42px;
  align-items: center;
  border-bottom: 1px solid #e5e5e5;
  background-color: #f7f7f7;
  color: #666;
  font-size: 13px;
  font-weight: 600;

  & > div {
    padding: 0 12px;
    text-align: center;
  }
`;

export const CustomTableRow = styled.div`
  display: grid;
  grid-template-columns:
    repeat(4, minmax(0, 1fr));
  min-width: 680px;
  min-height: 48px;
  align-items: center;
  border-bottom: 1px solid #eeeeee;
  background-color: #fff;
  color: #555;
  font-size: 14px;
  cursor: pointer;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background-color: #fffaf6;
  }

  & > div {
    padding: 0 12px;
    text-align: center;
  }
`;

export const CustomDetailTable = styled.div`
  margin: 0 14px 14px;
  overflow: hidden;
  border: 1px solid #f1d4c2;
  border-radius: 7px;
  background-color: #fff;
`;

export const CustomDetailHeader = styled.div`
  display: grid;
  grid-template-columns:
    minmax(180px, 0.35fr)
    minmax(360px, 0.65fr);
  min-width: 620px;
  min-height: 40px;
  align-items: center;
  border-bottom: 1px solid #f1d4c2;
  background-color: #fff8f1;
  color: #666;
  font-size: 13px;
  font-weight: 600;
`;

export const CustomDetailRow = styled.div`
  display: grid;
  grid-template-columns:
    minmax(180px, 0.35fr)
    minmax(360px, 0.65fr);
  min-width: 620px;
  min-height: 46px;
  align-items: center;
  border-bottom: 1px solid #eeeeee;
  color: #555;
  font-size: 13px;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background-color: #fffaf6;
  }
`;

export const CreativeGroup = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: center;
  padding: 9px 12px;

  & a {
    overflow: hidden;
    color: #f5811f;
    font-weight: 600;
    text-decoration: none;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const CreativeType = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 9px 12px;
`;

export const CreativeInfo = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  padding: 9px 12px;
  color: #777;
  word-break: break-all;
`;

export const CreativeImage = styled.div`
  display: inline-flex;
  width: 100px;
  height: 100px;
  align-items: center;
  justify-content: center;
  margin: 5px;
  padding: 5px;
  overflow: hidden;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background-color: #fafafa;

  & > img {
    display: block;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const CompactSearchHeader = styled.div`
  margin-bottom: 8px;
`;
